import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { Loader2, AlertTriangle } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Ecran de retour OAuth Google (Emergent-managed).
 * Detecte #session_id=<xxx> dans l'URL, l'echange contre un JWT interne,
 * stocke le JWT + user dans localStorage, hard-navigue vers /dashboard.
 * En cas d'erreur (email inconnu, session expiree), affiche un ecran clair
 * avec bouton "Retour a la connexion" au lieu de rediriger de force.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH.
 */
export default function AuthCallback() {
  const location = useLocation();
  const navigate = useNavigate();
  const { setToken, setUser } = useAuth();
  const hasProcessed = useRef(false);
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    // Anti-double-execution (StrictMode)
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = location.hash || "";
    const params = new URLSearchParams(hash.startsWith("#") ? hash.slice(1) : hash);
    const sessionId = params.get("session_id");

    if (!sessionId) {
      setErrorMsg("Session Google absente ou invalide.");
      return;
    }

    (async () => {
      try {
        const res = await axios.post(`${API}/auth/google/session`, { session_id: sessionId });
        const data = res.data;
        localStorage.setItem("odys_token", data.token);
        localStorage.setItem("odys_user", JSON.stringify(data));
        setToken(data.token);
        setUser(data);
        // Nettoie l'URL hash + redirect
        window.history.replaceState(null, "", "/dashboard");
        // Hard navigate pour forcer le refresh complet du context
        window.location.href = "/dashboard";
      } catch (err) {
        const detail = err?.response?.data?.detail || "Connexion Google echouee. Reessaie ou utilise ton email + mot de passe.";
        setErrorMsg(detail);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (errorMsg) {
    return (
      <div
        className="min-h-screen flex items-center justify-center px-4"
        style={{ background: "#FAF7F1" }}
        data-testid="google-auth-error"
      >
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 border-t-4"
             style={{ borderTopColor: "#C9A86A" }}>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-full flex items-center justify-center"
                 style={{ background: "#FEF3C7" }}>
              <AlertTriangle className="w-6 h-6 text-amber-600" />
            </div>
            <h1 className="text-xl font-bold" style={{ color: "#0F2540", fontFamily: "'Fraunces', serif" }}>
              Connexion Google impossible
            </h1>
          </div>
          <p className="text-sm mb-6 leading-relaxed" style={{ color: "#3D4B63" }}>
            {errorMsg}
          </p>
          <button
            onClick={() => {
              window.history.replaceState(null, "", "/login");
              navigate("/login", { replace: true });
            }}
            data-testid="back-to-login-btn"
            className="w-full py-3 rounded-lg font-semibold transition-colors"
            style={{ background: "#0F2540", color: "#FAF7F1" }}
          >
            Retour a la connexion
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen flex items-center justify-center"
      style={{ background: "#FAF7F1" }}
      data-testid="google-auth-loading"
    >
      <div className="flex flex-col items-center gap-4">
        <Loader2 className="w-10 h-10 animate-spin" style={{ color: "#C9A86A" }} />
        <p className="text-sm" style={{ color: "#3D4B63", fontFamily: "'Manrope', sans-serif" }}>
          Connexion Google en cours...
        </p>
      </div>
    </div>
  );
}
